import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";

import {
  Supplier,
  SupplierPayload,
} from "../types"; 
import { SupplierService } from "../api/supplier.service";

interface CityOption {
  id: string;
  name: string;
  state: string;
}

interface SupplierFormDialogProps {
  open: boolean;
  loading?: boolean;
  supplier?: Supplier | null;
  onClose: () => void;
  onSubmit: (data: SupplierPayload) => Promise<void>;
}

const defaultValues: SupplierPayload = {
  name: "",
  contactPerson: "",
  phone: "",
  email: "",
  gstNumber: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  remarks: "",
};

const emptyToUndefined = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const SupplierFormDialog = ({
  open,
  loading = false,
  supplier,
  onClose,
  onSubmit,
}: SupplierFormDialogProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
  } = useForm<SupplierPayload>({
    defaultValues,
  });
  const [cities, setCities] = useState<CityOption[]>([]);

  useEffect(() => {
    if (!open) return;

    SupplierService.getCityOptions()
      .then(setCities)
      .catch(() => setCities([]));
  }, [open]);

  useEffect(() => {
    if (supplier) {
      reset({
        name: supplier.name,
        contactPerson: supplier.contactPerson || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        gstNumber: supplier.gstNumber || "",
        address: supplier.address || "",
        city: supplier.city || "",
        state: supplier.state || "",
        pincode: supplier.pincode || "",
        remarks: supplier.remarks || "",
      });
    } else {
      reset(defaultValues);
    }
  }, [supplier, reset]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={onClose}
    >
      <DialogTitle>
        {supplier ? "Edit Supplier" : "Create Supplier"}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Supplier Name"
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="contactPerson"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Contact Person"
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Phone"
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Email"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="gstNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="GST Number"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={3}
                  label="Address"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="City"
                  helperText={
                    cities.length ? "" : "Cities are loading from City Management."
                  }
                  onChange={(event) => {
                    const city = cities.find((item) => item.name === event.target.value);
                    field.onChange(event.target.value);

                    if (city?.state) {
                      setValue("state", city.state);
                    }
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.name}>
                      {city.state ? `${city.name} (${city.state})` : city.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="State"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="pincode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Pincode"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={loading}
          onClick={handleSubmit((values) =>
            onSubmit({
              name: values.name.trim(),
              contactPerson: values.contactPerson.trim(),
              phone: values.phone.trim(),
              email: emptyToUndefined(values.email),
              gstNumber: emptyToUndefined(values.gstNumber),
              address: emptyToUndefined(values.address),
              city: emptyToUndefined(values.city),
              state: emptyToUndefined(values.state),
              pincode: emptyToUndefined(values.pincode),
              remarks: emptyToUndefined(values.remarks),
            })
          )}
        >
          {supplier ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierFormDialog;
